import type { Plan, TaskTemplate } from '../types'

/**
 * The household's chore plan, as a literal.
 *
 * This file is the whole of "the plan is code" — the data, and nothing else.
 * `src/lib/plan.ts` is the engine that reads it and knows nothing about where
 * it came from, which is what let the other half arrive without touching a line
 * of it: `buildPlan` folds the household's own chores (the synced `tasks`
 * collection) into this literal, and `src/App.tsx` is the one place the two
 * meet. See `docs/architecture-review.md` §1.3.
 *
 * What stays here is the standing rotation every household starts with. It is
 * *not* seed data — it ships in the bundle and is never written to Postgres, so
 * a fresh install has nothing it could push over a household that already has
 * history.
 *
 * **Task ids are permanent.** Every one below is embedded in the primary key of
 * every completion recorded against it. Change a `label` or an `emoji` freely;
 * renaming an `id` orphans a year of checkmarks, which stay in the database,
 * invisible, while the chore reappears unticked for every past day.
 */

const t = (
  id: string,
  label: string,
  emoji: string,
  category: string,
): TaskTemplate => ({ id, label, emoji, category })

const daily: TaskTemplate[] = [
  t('daily-aerate', 'Aérer les chambres', '🌬️', 'quotidien'),
  t('daily-kitchen', 'Remettre la cuisine en ordre', '🍽️', 'quotidien'),
  t('daily-tidy', 'Petit rangement de ce qui traîne', '🧺', 'quotidien'),
]

export const DEFAULT_PLAN: Plan = {
  daily,

  // Sunday first: the index is `Date.getDay()`, not a Monday-based week. The
  // month grid renders Monday first and shifts for it separately.
  weekly: [
    // Sunday — the day the week ahead is prepared.
    [
      t('sun-finish-laundry', 'Terminer, plier et ranger les lessives', '👕', 'linge'),
      t('sun-wc-bins', 'Vider les poubelles des WC', '🗑️', 'preparation'),
      t('sun-robot-clean', 'Nettoyer le robot aspirateur-laveur', '🤖', 'preparation'),
      t('sun-bread', 'Faire les pains pour la semaine', '🍞', 'preparation'),
      t('sun-biscuits', 'Faire les biscuits pour la semaine', '🍪', 'preparation'),
      t('sun-reset', 'Remettre la maison en ordre pour lundi', '🏡', 'preparation'),
    ],
    // Monday
    [t('mon-robot', 'Faire tourner le robot dans les pièces de vie', '🤖', 'sols')],
    // Tuesday
    [
      t('tue-mop-hall', 'Passer la mopette dans le couloir', '🧹', 'sols'),
      t('tue-mop-bedroom', 'Passer la mopette dans la chambre', '🧹', 'sols'),
    ],
    // Wednesday
    [
      t('wed-bathroom', 'Nettoyer la salle de bain', '🛁', 'salle-de-bain'),
      t('wed-towels', 'Changer les serviettes', '🧺', 'salle-de-bain'),
    ],
    // Thursday
    [
      t('thu-robot', 'Faire tourner le robot', '🤖', 'sols'),
      t('thu-dust', 'Faire les poussières rapides', '✨', 'grand-menage'),
    ],
    // Friday
    [
      t('fri-mop-living', 'Mopette cuisine et pièces de vie', '🧽', 'sols'),
      t('fri-main-bins', 'Vider les poubelles principales', '🗑️', 'quotidien'),
    ],
    // Saturday — the light day, because the rotation below lands on it.
    [t('sat-laundry', 'Faire les lessives pour être tranquille lundi', '🧺', 'linge')],
  ],

  // The four-week rotation, anchored on CYCLE_START. One deeper job per
  // Saturday, so nothing is left more than a month.
  saturdayRotation: [
    [
      t('rot-bedrooms', 'Nettoyage plus poussé des chambres', '🛏️', 'grand-menage'),
      t('rot-sheets', 'Changer les draps', '🛌', 'linge'),
    ],
    [
      t('rot-kitchen', 'Cuisine en profondeur', '🍳', 'grand-menage'),
      t('rot-fridge', 'Vérifier et nettoyer le frigo', '🧊', 'cuisine'),
    ],
    [t('rot-windows', 'Nettoyer les vitres principales et les miroirs', '🪟', 'grand-menage')],
    [t('rot-veranda', 'Nettoyer la véranda / entrée / zone tournante', '🌿', 'grand-menage')],
  ],

  monthlySunday: [
    t(
      'monthly-robot-deep',
      'Nettoyage complet du robot : brosses, roues, capteurs et bacs',
      '🧼',
      'grand-menage',
    ),
  ],

  // Empty, and it stays empty: a chore for one date only is by definition
  // something a household added itself, so it arrives through `buildPlan` from
  // the store. The slot is here because `Plan` is one type either way.
  oneOff: {},
}
